"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";

const BIRDIE_APP_ID = "0ardh9lh";
const BIRDIE_SCRIPT_ID = "birdie-websdk-script";
const BIRDIE_SCRIPT_SRC = `https://app.birdie.so/websdk/${BIRDIE_APP_ID}`;

function dispatchBirdieEvent(name: string, detail?: unknown) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function waitForBirdieWidget() {
  let timeoutId: number | undefined;
  let attempts = 0;
  let cancelled = false;

  function checkForWidget() {
    if (cancelled) return;

    if (window.birdie?.widget) {
      dispatchBirdieEvent("birdie:loaded");
      return;
    }

    attempts += 1;

    if (attempts < 100) {
      timeoutId = window.setTimeout(checkForWidget, 100);
    }
  }

  checkForWidget();

  return () => {
    cancelled = true;

    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  };
}

function appendBirdieScript() {
  if (window.birdie?.widget) {
    dispatchBirdieEvent("birdie:loaded");
    return () => {};
  }

  const stopWaitingForWidget = waitForBirdieWidget();

  const existingScript = document.getElementById(BIRDIE_SCRIPT_ID);

  if (existingScript) {
    return stopWaitingForWidget;
  }

  const script = document.createElement("script");
  script.id = BIRDIE_SCRIPT_ID;
  script.type = "text/javascript";
  script.async = true;
  script.src = BIRDIE_SCRIPT_SRC;

  document.body.appendChild(script);

  return stopWaitingForWidget;
}

export default function BirdieProvider() {
  const { data: session, status } = useSession();

  const userName = session?.user?.name ?? "";
  const userEmail = session?.user?.email ?? "";
  const userId =
    (session?.user as { id?: string | null } | undefined)?.id ?? "";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth <= 768) return;
    if (status !== "authenticated") return;
    if (!session?.user) return;
    if (!userEmail) return;

    window.recording_started_callback = () => {
      dispatchBirdieEvent("birdie:recording-started");
    };

    window.recording_stopped_callback = () => {
      dispatchBirdieEvent("birdie:recording-stopped");
    };

    window.recording_posted_callback = (link: string) => {
      dispatchBirdieEvent("birdie:recording-posted", link);
    };

    window.birdieSettings = {
      app_id: BIRDIE_APP_ID,
      contact_name: userName,
      contact_email: userEmail,
      contact_id: userId || userEmail,
      onRecordingStarted: window.recording_started_callback,
      onRecordingStopped: window.recording_stopped_callback,
      onRecordingPosted: window.recording_posted_callback,
    };

    const cleanupBirdieLoader = appendBirdieScript();

    return () => {
      cleanupBirdieLoader();
    };
  }, [status, session?.user, userName, userEmail, userId]);

  return null;
}
